// Section 22 explainability (Stream D): a number with its explain() factor list, as a disclosure
// that works with the keyboard (Enter or Space on the summary) and shows its focus ring. Takes a
// TooltipVM (viewModels/explain.ts), so the rows are the same evaluation the season used.
// Props only.
import type { TooltipVM } from '../../viewModels/explain';
import { societyText } from '../text/en';

interface Props {
  /** The number as shown. */
  value: string;
  tooltip: TooltipVM;
  /** What the number is, for screen readers ("Settlement trust"). */
  label: string;
}

function trim(x: number): string {
  return (Math.round(x * 100) / 100).toString();
}

function effect(op: string, value: number, delta: number): string {
  switch (op) {
    case 'base':
      return trim(value);
    case 'mul':
      return `x${trim(value)}`;
    case 'set':
      return `= ${trim(value)}`;
    case 'min':
      return `at most ${trim(value)}`;
    case 'max':
      return `at least ${trim(value)}`;
    default:
      return delta >= 0 ? `+${trim(delta)}` : trim(delta);
  }
}

export function FactorTip({ value, tooltip, label }: Props) {
  const rows = tooltip.groups.flatMap((g) => g.rows);
  return (
    <details class="society-tip">
      <summary class="num" aria-label={societyText.tipShow(label, value)}>
        {value}
      </summary>
      <table class="society-tip__factors">
        <caption class="visually-hidden">{label}</caption>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td>{societyText.tipEmpty}</td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i}>
                <th scope="row">{r.op === 'base' ? societyText.tipBase : (r.note ?? r.source)}</th>
                <td class="num">{effect(r.op, r.value, r.delta)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </details>
  );
}
